import teltonikaParams from "./teltonika-params.json";
import {teltonika} from "./teltonika";
import {dataManipulator, entityDataManager} from "./data-manipulation";
import i18n from "@/assets/js/vue/i18n";

export const positionAttributes = {
    engineRpmKey: 'engine-rpm',
    engineHoursKey: 'hours',
    fuelKey: 'fuel',
    speedKey: 'speed',
    odometerKey: 'odometer',
    ignitionKey: 'ignition',
    freezerKey: 'freezer',

    getAttributes() {
        const list = [];

        for (const teltonikaParam in teltonikaParams) {
            const paramInfo = teltonikaParams[teltonikaParam];
            if (!paramInfo.showInChart) {
                continue;
            }

            list.push({
                key: teltonikaParam,
                descriptionKey: `generic.params-map.${paramInfo.name}`,
            });
        }

        // Engine Rpm
        list.push({
            key: this.engineRpmKey,
            descriptionKey: 'generic.params-map.engine-rpm',
        });

        // Engine Hours
        list.push({
            key: this.engineHoursKey,
            descriptionKey: 'dashboard.quick-map.info-window.engine-hours',
            tooltipFormatFn: (value) => {
                const splitValue = value.split('.');
                const data = {hours: splitValue[0], minutes: splitValue[1]};
                return i18n.global.t('reports.trips.result.engine-hours', data);
            },
            yAxisFormatFn: (milliseconds) => {
                const result = dataManipulator.millisecondsToTime(milliseconds);
                return `${result.hours}.${result.minutes}`;
            },
            exportFormatFn: (milliseconds) => entityDataManager.formatEngineHours(milliseconds)
        });

        // Fuel
        list.push({
            key: this.fuelKey,
            descriptionKey: 'dashboard.quick-map.info-window.fuel',
        });

        // Speed
        list.push({
            key: this.speedKey,
            descriptionKey: 'dashboard.quick-map.info-window.speed',
        });

        // Speed
        list.push({
            key: this.odometerKey,
            descriptionKey: 'settings.car-fleet.form.odometer',
        });

        // Ignition
        list.push({
            key: this.ignitionKey,
            descriptionKey: 'dashboard.quick-map.info-window.ignition',
            showAsMarkArea: true,
            markAreaColor: 'rgba(80, 210, 126, 0.4)',
            tooltipFormatFn: (ignition) => i18n.global.t(ignition ? 'generic.yes' : 'generic.no'),
        });

        list.push({
            key: this.freezerKey,
            descriptionKey: 'dashboard.quick-map.info-window.freezer',
            showAsMarkArea: true,
            markAreaColor: 'rgba(255,37,37,0.4)',
            tooltipFormatFn: (freezer) => i18n.global.t(freezer ? 'generic.yes' : 'generic.no'),
        });

        return list;
    },

    processPositions(positions, vehicle, odometers) {
        return positions.map(position => this.processPosition(position, vehicle, odometers));
    },

    processPosition(position, vehicle, odometers) {
        const result = position;

        result['time'] = entityDataManager.getTimeByPosition(position);

        const positionAttributes = position['attributes'] || {};
        for (const teltonikaParam in teltonikaParams) {
            const paramInfo = teltonikaParams[teltonikaParam];
            if (!paramInfo.showInChart) {
                continue;
            }

            const modifier = paramInfo.chartModifier ?? paramInfo.modifier;
            result[teltonikaParam] = teltonika.parseParam(teltonikaParam, positionAttributes, modifier);
        }

        result[this.engineHoursKey] = entityDataManager.getEngineTimeByPosition(position);
        result[this.fuelKey] = entityDataManager.getFuelByPosition(position, vehicle?.tankCapacity) || null;
        result[this.ignitionKey] = entityDataManager.getPositionAttribute(position, 'ignition', false);
        result[this.freezerKey] = entityDataManager.getPositionAttribute(position, 'freezer', false);

        let engineRpm = entityDataManager.getPositionAttribute(position, 'rpm', null);
        if (engineRpm == null) {
            engineRpm = entityDataManager.getPositionAttribute(position, 'io85', null);
        }
        result[this.engineRpmKey] = engineRpm;

        let speed = entityDataManager.getPositionAttribute(position, 'io37', null);
        if (speed == null) {
            speed = entityDataManager.getSpeedInKmphByPosition(position);
        }

        speed = speed?.toFixed(2);
        result[this.speedKey] = speed;

        let odometer = null;
        if (odometers) {
            const positionDate = entityDataManager.getTimeByPosition(position);
            const positionOdometer = odometers.find(item => positionDate.isSame(item.dateOfRecord, 'day'));
            odometer = positionOdometer?.odometerRounded;
        }

        result[this.odometerKey] = odometer;

        return result;
    }
}